import React, { useEffect, useState } from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import AlertDismissible from '../../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import PaginationList from '../../../Shared/Components/PaginationList'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import axios from 'axios'
import { SIA_NAVY } from '../../../constants/siaTheme'

function formatDate(val) {
    if (!val) return '—'
    const d = String(val).slice(0, 10)
    const [y, m, day] = d.split('-')
    if (!y || !m || !day) return val
    return `${day}/${m}/${y}`
}

function formatTime(val) {
    if (!val) return '—'
    const parts = String(val).slice(0, 8).split(':')
    const hour24 = Number.parseInt(parts[0], 10)
    const minutes = parts[1] ?? '00'
    if (Number.isNaN(hour24)) return String(val)

    const suffix = hour24 >= 12 ? 'PM' : 'AM'
    let hour12 = hour24 % 12
    if (hour12 === 0) hour12 = 12

    return `${String(hour12).padStart(2, '0')}:${minutes} ${suffix}`
}

function fullName(row) {
    return [row.nombre, row.apellido_paterno, row.apellido_materno].filter(Boolean).join(' ')
}

function isClosedStatus(statuses, statusId) {
    const found = (statuses || []).find((s) => String(s.id) === String(statusId))
    return found && (found.code === 'terminado' || found.code === 'rechazado')
}

function HistoryModal({ show, onHide, logs, recordLabel, canDeleteLog, onDeleted }) {
    const [deletingId, setDeletingId] = useState(null)

    const softDelete = async (logId) => {
        if (!canDeleteLog || !window.confirm('¿Eliminar esta entrada de bitácora?')) return
        setDeletingId(logId)
        try {
            await axios.delete(route('customer-service.follow-up.destroy', logId))
            onDeleted?.(logId)
        } finally {
            setDeletingId(null)
        }
    }

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Bitácora — {recordLabel || 'trámite'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {(!logs || logs.length === 0) ? (
                    <p className="text-muted mb-0">Sin registros de seguimiento.</p>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-3">
                            <thead>
                                <tr className="fw-bold text-muted text-uppercase fs-7 bg-light">
                                    <th className="ps-4">Fecha</th>
                                    <th>Hora</th>
                                    <th>Registró</th>
                                    <th>Estado</th>
                                    <th>Observaciones</th>
                                    {canDeleteLog && <th className="pe-4 text-end">Acciones</th>}
                                </tr>
                            </thead>
                            <tbody className="fs-7">
                                {logs.map((log) => (
                                    <tr key={log.id}>
                                        <td className="ps-4">{formatDate(log.follow_up_date)}</td>
                                        <td>{formatTime(log.follow_up_time)}</td>
                                        <td>{log.contacted_by || '—'}</td>
                                        <td>{log.status_name || '—'}</td>
                                        <td style={{ whiteSpace: 'pre-wrap' }}>{log.observations || '—'}</td>
                                        {canDeleteLog && (
                                            <td className="pe-4 text-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-light-danger"
                                                    disabled={deletingId === log.id}
                                                    onClick={() => softDelete(log.id)}
                                                >
                                                    {deletingId === log.id ? '…' : 'Eliminar'}
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    )
}

function FollowUpModal({ show, onHide, record, statuses, defaultDate, defaultTime }) {
    const form = useForm({
        follow_up_date: defaultDate || '',
        follow_up_time: defaultTime || '',
        sia_customer_service_status_id: '',
        observations: '',
    })

    useEffect(() => {
        if (show && record) {
            form.setData({
                follow_up_date: defaultDate || '',
                follow_up_time: defaultTime || '',
                sia_customer_service_status_id: '',
                observations: '',
            })
            form.clearErrors()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, record?.id, defaultDate, defaultTime])

    const closedRequired = isClosedStatus(statuses, form.data.sia_customer_service_status_id)

    const submit = (e) => {
        e.preventDefault()
        if (!record) return
        form.post(route('customer-service.follow-up.store', record.id), {
            preserveScroll: true,
            onSuccess: () => onHide(),
        })
    }

    return (
        <Modal show={show} onHide={() => !form.processing && onHide()} size="lg" centered backdrop="static">
            <Form onSubmit={submit}>
                <Modal.Header closeButton={!form.processing}>
                    <Modal.Title>Agregar seguimiento</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {record && (
                        <p className="text-muted fs-7 mb-6">
                            <span className="fw-semibold text-gray-800">{fullName(record)}</span>
                            {record.numero_poliza ? ` — Póliza ${record.numero_poliza}` : ''}
                        </p>
                    )}
                    <Row className="g-4">
                        <Col md={6}>
                            <Form.Group className="mb-4">
                                <Form.Label className="fw-bold">Fecha <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    value={form.data.follow_up_date}
                                    onChange={(e) => form.setData('follow_up_date', e.target.value)}
                                    isInvalid={!!form.errors.follow_up_date}
                                    disabled={form.processing}
                                />
                                <Form.Control.Feedback type="invalid">{form.errors.follow_up_date}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-4">
                                <Form.Label className="fw-bold">Hora <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="time"
                                    value={form.data.follow_up_time}
                                    onChange={(e) => form.setData('follow_up_time', e.target.value)}
                                    isInvalid={!!form.errors.follow_up_time}
                                    disabled={form.processing}
                                />
                                <Form.Control.Feedback type="invalid">{form.errors.follow_up_time}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-0">
                                <Form.Label className="fw-bold">Estado <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    value={form.data.sia_customer_service_status_id}
                                    onChange={(e) => form.setData('sia_customer_service_status_id', e.target.value)}
                                    isInvalid={!!form.errors.sia_customer_service_status_id}
                                    disabled={form.processing}
                                >
                                    <option value="">Seleccione una opción</option>
                                    {(statuses || []).map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{form.errors.sia_customer_service_status_id}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-0 h-100 d-flex flex-column">
                                <Form.Label className="fw-bold">
                                    Observaciones {closedRequired && <span className="text-danger">*</span>}
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={8}
                                    className="flex-grow-1"
                                    value={form.data.observations}
                                    onChange={(e) => form.setData('observations', e.target.value)}
                                    isInvalid={!!form.errors.observations}
                                    disabled={form.processing}
                                    placeholder={closedRequired ? 'Indica la razón de terminación o rechazo' : ''}
                                />
                                <Form.Control.Feedback type="invalid">{form.errors.observations}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={onHide} disabled={form.processing}>
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        disabled={form.processing}
                        style={{ backgroundColor: SIA_NAVY, borderColor: SIA_NAVY }}
                    >
                        {form.processing ? 'Guardando…' : 'Registrar'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    )
}

const CustomerServiceIndex = ({
    entries,
    filters,
    mode,
    canEdit,
    canExport = false,
    canCreateLog = false,
    canViewLog = false,
    canDeleteLog = false,
    advisers,
    visitTypes,
    statuses = [],
    defaultFollowUpDate,
    defaultFollowUpTime,
}) => {
    const { flash } = usePage().props
    const manage = mode === 'manage'
    const readonly = mode === 'readonly'
    const [deleteTarget, setDeleteTarget] = useState(null)
    const [deleting, setDeleting] = useState(false)
    const [history, setHistory] = useState({ show: false, logs: [], label: '' })
    const [followUp, setFollowUp] = useState({ show: false, record: null })

    const filterForm = useForm({
        fecha_inicio: filters.fecha_inicio || '',
        fecha_fin: filters.fecha_fin || '',
        sia_visit_type_id: filters.sia_visit_type_id || '',
        sia_customer_service_status_id: filters.sia_customer_service_status_id || '',
        numero_poliza: filters.numero_poliza || '',
        asegurado: filters.asegurado || '',
        adviser_id: filters.adviser_id || '',
    })

    const indexRoute = 'customer-service.index'
    const pathPrefix = 'admin/atencion-cliente/registros'

    const submitFilters = (e) => {
        e.preventDefault()
        filterForm.get(route(indexRoute), { preserveState: true })
    }

    const clearFilters = () => {
        filterForm.setData({
            fecha_inicio: '',
            fecha_fin: '',
            sia_visit_type_id: '',
            sia_customer_service_status_id: '',
            numero_poliza: '',
            asegurado: '',
            adviser_id: '',
        })
        filterForm.get(route(indexRoute))
    }

    const paginationFilters = {
        fecha_inicio: filters.fecha_inicio || undefined,
        fecha_fin: filters.fecha_fin || undefined,
        sia_visit_type_id: filters.sia_visit_type_id || undefined,
        sia_customer_service_status_id: filters.sia_customer_service_status_id || undefined,
        numero_poliza: filters.numero_poliza || undefined,
        asegurado: filters.asegurado || undefined,
        adviser_id: filters.adviser_id || undefined,
    }

    const exportUrl = () => {
        const params = new URLSearchParams()
        Object.entries(filterForm.data).forEach(([key, value]) => {
            if (value !== null && value !== undefined && String(value) !== '') {
                params.set(key, String(value))
            }
        })
        const qs = params.toString()
        return route('customer-service.export') + (qs ? `?${qs}` : '')
    }

    const confirmDelete = () => {
        if (!deleteTarget) return
        setDeleting(true)
        Inertia.delete(route('customer-service.destroy', deleteTarget.id), {
            preserveScroll: true,
            onFinish: () => {
                setDeleting(false)
                setDeleteTarget(null)
            },
        })
    }

    const openHistory = async (row) => {
        const { data } = await axios.get(route('customer-service.follow-up.history', row.id))
        setHistory({
            show: true,
            logs: data.logs || [],
            label: data.record_label || fullName(row),
        })
    }

    const data = entries.data || []
    const createHref = route('customer-service.create')
    const editHref = (row) => route('customer-service.edit', row.id)
    const showActions = canEdit || canCreateLog || canViewLog
    const colSpan = showActions ? 17 : 16

    return (
        <Row>
            {flash?.success && <AlertDismissible message={flash.success} type="success" />}
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath={pathPrefix} />
            </span>

            <div
                className="rounded-2 mb-8 py-4 px-4 text-center text-white text-uppercase fw-bold fs-4"
                style={{ backgroundColor: SIA_NAVY, letterSpacing: '0.04em' }}
            >
                Información sobre los visitantes
            </div>

            <div className="card card-flush h-md-100">
                <div className="card-body pt-6">
                    <Form onSubmit={submitFilters} className="mb-8">
                        <Row className="g-4 align-items-end">
                            <div className="col-md-3">
                                <Form.Label className="fw-bold fs-7">Rango de fechas (inicio)</Form.Label>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={filterForm.data.fecha_inicio}
                                    onChange={(e) => filterForm.setData('fecha_inicio', e.target.value)}
                                />
                            </div>
                            <div className="col-md-3">
                                <Form.Label className="fw-bold fs-7">Rango de fechas (fin)</Form.Label>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={filterForm.data.fecha_fin}
                                    onChange={(e) => filterForm.setData('fecha_fin', e.target.value)}
                                />
                            </div>
                            <div className="col-md-3">
                                <Form.Label className="fw-bold fs-7">Tipo de visita</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={filterForm.data.sia_visit_type_id}
                                    onChange={(e) => filterForm.setData('sia_visit_type_id', e.target.value)}
                                >
                                    <option value="">Seleccione una opción</option>
                                    {(visitTypes || []).map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </Form.Select>
                            </div>
                            <div className="col-md-3">
                                <Form.Label className="fw-bold fs-7">Estado</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={filterForm.data.sia_customer_service_status_id}
                                    onChange={(e) => filterForm.setData('sia_customer_service_status_id', e.target.value)}
                                >
                                    <option value="">Seleccione una opción</option>
                                    {(statuses || []).map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </Form.Select>
                            </div>
                            <div className="col-md-3">
                                <Form.Label className="fw-bold fs-7">Póliza</Form.Label>
                                <Form.Control
                                    size="sm"
                                    placeholder="Ejemplo: 1234567890"
                                    value={filterForm.data.numero_poliza}
                                    onChange={(e) => filterForm.setData('numero_poliza', e.target.value)}
                                />
                            </div>
                            <div className="col-md-3">
                                <Form.Label className="fw-bold fs-7">Asegurado</Form.Label>
                                <Form.Control
                                    size="sm"
                                    placeholder="Ejemplo: Juan Pérez Vásquez"
                                    value={filterForm.data.asegurado}
                                    onChange={(e) => filterForm.setData('asegurado', e.target.value)}
                                />
                            </div>
                            <div className="col-md-3">
                                <Form.Label className="fw-bold fs-7">Asesor</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={filterForm.data.adviser_id}
                                    onChange={(e) => filterForm.setData('adviser_id', e.target.value)}
                                >
                                    <option value="">Seleccione una opción</option>
                                    {(advisers || []).map((a) => (
                                        <option key={a.id} value={a.id}>{a.display_name}</option>
                                    ))}
                                </Form.Select>
                            </div>
                            <div className="col-md-3 d-flex gap-2">
                                <Button type="submit" variant="primary" size="sm" disabled={filterForm.processing}>
                                    Mostrar resultados
                                </Button>
                                <Button type="button" variant="light" size="sm" onClick={clearFilters}>
                                    Limpiar
                                </Button>
                            </div>
                        </Row>
                    </Form>

                    <div className="d-flex justify-content-between align-items-center mb-6 flex-wrap gap-2">
                        <div className="d-flex gap-2">
                            {(canExport || manage) && (
                                <a className="btn btn-sm btn-success" href={exportUrl()}>
                                    <i className="bi bi-file-earmark-excel me-1" />
                                    Exportar Excel
                                </a>
                            )}
                        </div>
                        {canEdit && !readonly && (
                            <Link href={createHref} className="btn btn-sm btn-primary">
                                Agregar registro
                            </Link>
                        )}
                    </div>

                    <div className="table-responsive">
                        <table className="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-3 text-uppercase fs-8">
                            <thead>
                                <tr className="fw-bold text-white" style={{ backgroundColor: SIA_NAVY }}>
                                    <th className="ps-3">Oficina</th>
                                    <th>Ejecutiva</th>
                                    <th>Fecha</th>
                                    <th>Hora inicio</th>
                                    <th>Hora término</th>
                                    <th>Asegurado</th>
                                    <th>Trámite</th>
                                    <th>Estado</th>
                                    <th>Teléfono</th>
                                    <th>Correo</th>
                                    <th>Póliza</th>
                                    <th>RFC</th>
                                    <th>Monto (dividendos)</th>
                                    <th>Escuela</th>
                                    <th>Observaciones</th>
                                    <th>Asesor que entregó</th>
                                    {showActions && <th className="pe-3 text-end">Acciones</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {data.length === 0 && (
                                    <tr>
                                        <td colSpan={colSpan} className="text-center py-10 text-gray-700">
                                            No hay registros.
                                        </td>
                                    </tr>
                                )}
                                {data.map((row, idx) => (
                                    <tr key={row.id} className={idx % 2 === 1 ? 'bg-light' : ''}>
                                        <td className="ps-3">{row.office_name || '—'}</td>
                                        <td>{row.user?.display_name || row.user?.name || '—'}</td>
                                        <td>{formatDate(row.fecha)}</td>
                                        <td>{formatTime(row.hora_inicio)}</td>
                                        <td>{formatTime(row.hora_termino)}</td>
                                        <td className="fw-semibold">{fullName(row)}</td>
                                        <td>{row.visit_type?.name || '—'}</td>
                                        <td>{row.status?.name || '—'}</td>
                                        <td>{row.telefono}</td>
                                        <td>{row.email}</td>
                                        <td>{row.numero_poliza || '—'}</td>
                                        <td>{row.rfc || '—'}</td>
                                        <td>{row.cantidad ?? '0'}</td>
                                        <td>{row.nombre_escuela || '—'}</td>
                                        <td>
                                            <span className="text-truncate d-inline-block" style={{ maxWidth: '140px' }}>
                                                {row.observaciones || '—'}
                                            </span>
                                        </td>
                                        <td>{row.adviser_display_name || '—'}</td>
                                        {showActions && (
                                            <td className="text-end pe-3">
                                                <div className="d-flex justify-content-end gap-1 flex-wrap">
                                                    {canEdit && (
                                                        <Link href={editHref(row)} className="btn btn-sm btn-light-primary">
                                                            Editar
                                                        </Link>
                                                    )}
                                                    {canCreateLog && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-icon btn-light-primary"
                                                            title="Agregar seguimiento"
                                                            onClick={() => setFollowUp({ show: true, record: row })}
                                                        >
                                                            <i className="bi bi-plus-lg" />
                                                        </button>
                                                    )}
                                                    {canViewLog && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-icon btn-light-primary"
                                                            title="Ver historial"
                                                            onClick={() => openHistory(row)}
                                                        >
                                                            <i className="bi bi-list-ul" />
                                                        </button>
                                                    )}
                                                    {canEdit && manage && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-light-danger"
                                                            onClick={() => setDeleteTarget(row)}
                                                        >
                                                            Eliminar
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-center py-6">
                        <PaginationList links={entries.links} filters={paginationFilters} />
                    </div>
                </div>
            </div>

            {manage && (
                <Modal show={deleteTarget !== null} onHide={() => !deleting && setDeleteTarget(null)} centered>
                    <Modal.Header closeButton={!deleting}>
                        <Modal.Title>Confirmar eliminación</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        ¿Eliminar el registro de <strong>{deleteTarget ? fullName(deleteTarget) : ''}</strong>?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" disabled={deleting} onClick={() => setDeleteTarget(null)}>
                            Cancelar
                        </Button>
                        <Button variant="danger" disabled={deleting} onClick={confirmDelete}>
                            {deleting ? 'Eliminando…' : 'Eliminar'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            <HistoryModal
                show={history.show}
                onHide={() => setHistory({ show: false, logs: [], label: '' })}
                logs={history.logs}
                recordLabel={history.label}
                canDeleteLog={canDeleteLog}
                onDeleted={(id) => setHistory((h) => ({ ...h, logs: h.logs.filter((l) => l.id !== id) }))}
            />

            <FollowUpModal
                show={followUp.show}
                onHide={() => setFollowUp({ show: false, record: null })}
                record={followUp.record}
                statuses={statuses}
                defaultDate={defaultFollowUpDate}
                defaultTime={defaultFollowUpTime}
            />
        </Row>
    )
}

CustomerServiceIndex.layout = (page) => <Layout title="Atención al cliente">{page}</Layout>

export default CustomerServiceIndex
export { HistoryModal, formatDate, formatTime, fullName }
